// File: TeamEdit.js

import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SketchPicker } from "react-color";
import { axiosInstance } from "../../../utils/axios";
import { useSpace } from "../../../contexts/SpaceContext";
import MenuList from "@mui/material/MenuList";

import {
  Avatar as MuiAvatar,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Chip as MuiChip,
  Divider as MuiDivider,
  Grid,
  Paper as MuiPaper,
  Toolbar,
  Typography,
  Snackbar,
  Alert,
  Card as MuiCard,
  MenuItem,
  ListItemText,
  Tab,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { Link } from "react-router-dom";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

// Import the team tab components
import Profile from "./Profile";
import Members from "./Members";
import Appearance from "./Appearance";

// GraphQL queries
const GET_TEAM = `
  query GetTeam($id: ID!) {
    getTeam(id: $id) {
      id
      name
      url
      about
      logo
      coverImage
      color
      members {
        id
        userId
        email
        role
        status
      }
      settings {
        allowMemberInvites
        visibility
        joinRequests
        userImpersonation
        privateTeam
      }
      customBrandColors
      lightThemeColor
      darkThemeColor
      hideInstaconnectBranding
      hideBookTeamMemberButton
      theme
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_MUTATION = `
  mutation UpdateTeam($input: UpdateTeamInput!) {
    updateTeam(input: $input) {
      team {
        id
        name
        url
        about
        logo
        coverImage
        color
        settings {
          allowMemberInvites
          visibility
          joinRequests
          userImpersonation
          privateTeam
        }
        customBrandColors
        lightThemeColor
        darkThemeColor
        hideInstaconnectBranding
        hideBookTeamMemberButton
        theme
      }
    }
  }
`;

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;

// Team Edit Tabs Component
function TeamEditTabs({ team, onTeamUpdate }) {
  const [activeTab, setActiveTab] = useState("Profile");
  const navigate = useNavigate();

  // Render tabs menu for sidebar
  const renderTabMenu = () => {
    const tabs = [
      { id: "Profile", label: "Profile" },
      { id: "Members", label: "Members" },
      { id: "Appearance", label: "Appearance" },
    ];

    return (
      <Paper>
        <MenuList style={{ height: "auto", minHeight: "400px" }}>
          {tabs.map((tab) => (
            <MenuItem
              key={tab.id}
              selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                borderLeft: activeTab === tab.id ? "3px solid" : "none",
                borderColor: "primary.main",
                backgroundColor:
                  activeTab === tab.id ? "rgba(0, 0, 0, 0.04)" : "transparent",
                minHeight: "50px",
                paddingLeft: "16px",
                fontWeight: activeTab === tab.id ? "bold" : "normal",
              }}
            >
              <ListItemText primary={tab.label} />
              {activeTab === tab.id && (
                <KeyboardArrowRightIcon color="primary" />
              )}
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    );
  };

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "Profile":
        return <Profile team={team} onUpdate={onTeamUpdate} />;
      case "Members":
        return <Members team={team} onUpdate={onTeamUpdate} />;
      case "Appearance":
        return <Appearance team={team} onUpdate={onTeamUpdate} />;
      default:
        return null;
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        {renderTabMenu()}
      </Grid>
      <Grid item xs={12} md={9}>
        {renderTabContent()}
      </Grid>
    </Grid>
  );
}

function TeamEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentSpace } = useSpace();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Fetch team data
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.post("/graphql", {
          query: GET_TEAM,
          variables: { id },
        });

        if (response.errors) {
          console.error("GraphQL errors:", response.errors);
          handleNotification("Failed to load team data", "error");
          navigate("/calendar/teams");
        } else {
          setTeam(
            response.data.data
              ? response.data.data.getTeam
              : response.data.getTeam
          );
        }
      } catch (error) {
        console.error("Error fetching team:", error);
        handleNotification("Failed to load team data", "error");
        navigate("/calendar/teams");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTeam();
    }
  }, [id, navigate]);

  // Handle team update
  const handleTeamUpdate = async (updatedData) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/graphql", {
        query: UPDATE_TEAM_MUTATION,
        variables: {
          input: {
            id: team.id,
            ...updatedData,
          },
        },
      });

      if (response.errors) {
        console.error("GraphQL errors:", response.errors);
        handleNotification("Failed to update team", "error");
      } else {
        const updatedTeam = response.data.updateTeam.team;
        setTeam({ ...team, ...updatedTeam });
        handleNotification("Team updated successfully", "success");
      }
    } catch (error) {
      console.error("Error updating team:", error);
      handleNotification("Failed to update team", "error");
    } finally {
      setLoading(false);
    }
  };

  // Notification handling
  const handleNotification = (message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  if (loading && !team) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">Loading team data...</Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="Edit Team" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Edit Team
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${currentSpace?.id}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link component={NavLink} to="/calendar/teams">
              Teams List
            </Link>
            <Typography>{team?.name || "Edit Team"}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            to="/calendar/teams"
            startIcon={<ArrowBackIcon />}
          >
            Back to Teams
          </Button>
        </Grid>
      </Grid>

      <Divider my={6} />

      {team && <TeamEditTabs team={team} onTeamUpdate={handleTeamUpdate} />}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default TeamEdit;
